import { useMemo } from 'react'
import { Box, Typography, IconButton, Paper, Skeleton, Chip, Tooltip } from '@mui/material'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import PrintIcon from '@mui/icons-material/Print'
import HistoryIcon from '@mui/icons-material/History'
import dayjs from 'dayjs'
import RosterSlotCard from './RosterSlotCard'
import { EVENT_TYPES_ARRAY, getEventTypeObject } from 'src/consumer/constants'

export default function DailyView({
  rosterData,
  unoccupiedSlots,
  selectedDate,
  onDateChange,
  onSlotClick,
  onEmptySlotClick,
  loading,
  rooms,
  legendItems,
  chamberRoomsData = new Map(), // Map of roomId -> room data with rosterSchedule
  targetSpecialities = [], // Array of target speciality objects with slug and title
  mentorDataMap = new Map(), // Map of mentorId -> mentor data (including profilePicId)
}) {
  const dateStr = selectedDate.format('YYYY-MM-DD')
  const dayOfWeek = selectedDate.format('dddd').toLowerCase()
  const isPastDate = selectedDate.isBefore(dayjs().startOf('day'))

  // Get unique rooms
  const uniqueRooms = useMemo(() => {
    if (rooms && rooms.length > 0) return rooms

    const roomSet = new Set()
    rosterData.forEach((slot) => {
      if (slot.chamberRoomId && slot.customDate === dateStr) {
        roomSet.add(slot.chamberRoomId)
      }
    })
    unoccupiedSlots.forEach((slot) => {
      if (slot.chamberRoomId && slot.customDate === dateStr) {
        roomSet.add(slot.chamberRoomId)
      }
    })

    return Array.from(roomSet).map((id) => ({
      id,
      title: `Room ${id}`,
    }))
  }, [rooms, rosterData, unoccupiedSlots, dateStr])

  // Group rooms by target speciality → event type → clinic brand → clinic location
  const roomsBySpeciality = useMemo(() => {
    const roomsToProcess = uniqueRooms.length > 0 ? uniqueRooms : []
    
    // Build structured grouping: { targetSpeciality: { eventType: [rooms] } }
    const grouped = {}
    
    roomsToProcess.forEach((room) => {
      const roomData = chamberRoomsData.get(room.id)
      const rosterSchedule = roomData?.rosterSchedule
      const clinicBrandName = room.clinicBrandName || roomData?.clinicBrand?.name || roomData?.clinicBrandName || 'Unknown Brand'
      const clinicLocationName = room.clinicLocationName || roomData?.clinicLocation?.name || roomData?.clinicLocationName || 'Unknown Location'
      
      // Get primary target speciality (first one, or 'General' if none)
      const targetSpecialitySlugs = rosterSchedule?.targetSpecialitySlugs || []
      const primarySpeciality = targetSpecialitySlugs.length > 0 ? targetSpecialitySlugs[0] : 'general'
      
      // Get primary event type (first one, or 'other' if none)
      const eventTypes = rosterSchedule?.eventTypes || []
      const primaryEventType = eventTypes.length > 0 ? eventTypes[0] : 'other'
      
      if (!grouped[primarySpeciality]) {
        grouped[primarySpeciality] = {}
      }
      if (!grouped[primarySpeciality][primaryEventType]) {
        grouped[primarySpeciality][primaryEventType] = []
      }
      
      grouped[primarySpeciality][primaryEventType].push({
        ...room,
        clinicBrandName,
        clinicLocationName,
        primarySpeciality,
        primaryEventType,
        eventTypes,
        targetSpecialitySlugs,
      })
    })
    
    // Convert to structured array with separate tables per speciality
    const result = []
    const specialityKeys = Object.keys(grouped).sort()
    
    specialityKeys.forEach((specialitySlug) => {
      const eventTypeGroups = grouped[specialitySlug]
      const eventTypeKeys = Object.keys(eventTypeGroups).sort()
      const roomsForSpeciality = []
      
      eventTypeKeys.forEach((eventType, eventTypeIndex) => {
        const rooms = eventTypeGroups[eventType]
        
        // Sort rooms by clinic brand and location
        rooms.sort((a, b) => {
          if (a.clinicBrandName !== b.clinicBrandName) {
            return a.clinicBrandName.localeCompare(b.clinicBrandName)
          }
          return a.clinicLocationName.localeCompare(b.clinicLocationName)
        })
        
        rooms.forEach((room, roomIndex) => {
          roomsForSpeciality.push({
            ...room,
            isFirstInEventType: roomIndex === 0,
            isLastInEventType: roomIndex === rooms.length - 1,
            eventTypeIndex,
          })
        })
      })
      
      result.push({
        specialitySlug,
        rooms: roomsForSpeciality,
      })
    })
    
    return result
  }, [uniqueRooms, chamberRoomsData])

  // Group and sort slots by room
  const slotsByRoom = useMemo(() => {
    const grouped = new Map()

    uniqueRooms.forEach((room) => {
      const roomSlots = []
      
      // Get all slots for this room and date
      rosterData.forEach((slot) => {
        if (slot.chamberRoomId === room.id && slot.customDate === dateStr) {
          roomSlots.push(slot)
        }
      })

      // Sort by start time
      roomSlots.sort((a, b) => {
        const timeA = dayjs(a.slotStartTime, 'HH:mm')
        const timeB = dayjs(b.slotStartTime, 'HH:mm')
        return timeA.isBefore(timeB) ? -1 : timeA.isAfter(timeB) ? 1 : 0
      })

      grouped.set(room.id, roomSlots)
    })

    return grouped
  }, [uniqueRooms, rosterData, dateStr])

  // ==================== COLOR SYSTEM ====================
  
  // Event Type colors (for chips and borders)
  const eventTypeColors = {
    'therapy_session': { bg: '#E3F2FD', text: '#1565C0', border: '#1976D2' }, // Blue
    'screening': { bg: '#F3E5F5', text: '#7B1FA2', border: '#9C27B0' }, // Purple
    'consultation': { bg: '#E8F5E9', text: '#2E7D32', border: '#4CAF50' }, // Green
    'follow_up': { bg: '#FFF3E0', text: '#E65100', border: '#FF9800' }, // Orange
  }
  
  // Target Speciality colors (for chips and borders)
  const targetSpecialityColors = {
    'psychiatry': { bg: '#FCE4EC', text: '#C2185B', border: '#E91E63' }, // Pink
    'endocrinology': { bg: '#E0F7FA', text: '#00838F', border: '#00BCD4' }, // Cyan
    'cardiology': { bg: '#FFEBEE', text: '#C62828', border: '#EF5350' }, // Red
    'neurology': { bg: '#EDE7F6', text: '#512DA8', border: '#673AB7' }, // Deep Purple
    'dermatology': { bg: '#FFF8E1', text: '#FF8F00', border: '#FFC107' }, // Amber
  }
  
  // Get color for event type chip
  const getEventTypeChipColor = (eventTypeId) => {
    return eventTypeColors[eventTypeId] || { bg: '#ECEFF1', text: '#546E7A', border: '#78909C' }
  }
  
  // Get color for target speciality chip
  const getTargetSpecialityChipColor = (slug) => {
    return targetSpecialityColors[slug] || { bg: '#ECEFF1', text: '#546E7A', border: '#78909C' }
  }
  
  // Get event type border color for horizontal separators
  const getEventTypeBorderColor = (eventType) => {
    return eventTypeColors[eventType]?.border || '#90A4AE'
  }
  
  // Get target speciality header color
  const getSpecialityHeaderColor = (slug) => {
    return targetSpecialityColors[slug] || { bg: '#ECEFF1', text: '#546E7A', border: '#78909C' }
  }
  
  // Mentor color palette - distinct colors that work well with glass effect
  const mentorColorPalette = [
    { bg: 'rgba(33, 150, 243, 0.85)', text: '#fff' },   // Blue
    { bg: 'rgba(156, 39, 176, 0.85)', text: '#fff' },   // Purple
    { bg: 'rgba(0, 150, 136, 0.85)', text: '#fff' },    // Teal
    { bg: 'rgba(255, 87, 34, 0.85)', text: '#fff' },    // Deep Orange
    { bg: 'rgba(76, 175, 80, 0.85)', text: '#fff' },    // Green
    { bg: 'rgba(63, 81, 181, 0.85)', text: '#fff' },    // Indigo
    { bg: 'rgba(233, 30, 99, 0.85)', text: '#fff' },    // Pink
    { bg: 'rgba(0, 188, 212, 0.85)', text: '#fff' },    // Cyan
    { bg: 'rgba(121, 85, 72, 0.85)', text: '#fff' },    // Brown
    { bg: 'rgba(103, 58, 183, 0.85)', text: '#fff' },   // Deep Purple
    { bg: 'rgba(255, 152, 0, 0.85)', text: '#fff' },    // Orange
    { bg: 'rgba(96, 125, 139, 0.85)', text: '#fff' },   // Blue Grey
    { bg: 'rgba(244, 67, 54, 0.85)', text: '#fff' },    // Red
    { bg: 'rgba(139, 195, 74, 0.85)', text: '#fff' },   // Light Green
    { bg: 'rgba(3, 169, 244, 0.85)', text: '#fff' },    // Light Blue
  ]
  
  // Get consistent color for a mentor based on their ID
  const getMentorColor = (mentorId) => {
    if (!mentorId) return { bg: 'rgba(158, 158, 158, 0.85)', text: '#fff' } // Grey for unassigned
    
    // Generate a consistent index based on mentor ID hash
    const hash = String(mentorId).split('').reduce((acc, char) => {
      return char.charCodeAt(0) + ((acc << 5) - acc)
    }, 0)
    
    const index = Math.abs(hash) % mentorColorPalette.length
    return mentorColorPalette[index]
  }
  
  // Pure Red → Yellow → Green gradient for coverage (no blending)
  const getCoverageColor = (percentage) => {
    // Color stops: Red (0%) -> Yellow (50%) -> Green (100%)
    const redColor = { r: 255, g: 235, b: 238 } // #ffebee - Light red
    const yellowColor = { r: 255, g: 249, b: 196 } // #fff9c4 - Light yellow
    const greenColor = { r: 232, g: 245, b: 233 } // #e8f5e9 - Light green

    if (percentage === 0) {
      return `rgb(${redColor.r}, ${redColor.g}, ${redColor.b})`
    }
    if (percentage === 100) {
      return `rgb(${greenColor.r}, ${greenColor.g}, ${greenColor.b})`
    }

    let r, g, b
    if (percentage <= 50) {
      // Interpolate between red and yellow (0% -> 50%)
      const t = percentage / 50
      r = Math.round(redColor.r + (yellowColor.r - redColor.r) * t)
      g = Math.round(redColor.g + (yellowColor.g - redColor.g) * t)
      b = Math.round(redColor.b + (yellowColor.b - redColor.b) * t)
    } else {
      // Interpolate between yellow and green (50% -> 100%)
      const t = (percentage - 50) / 50
      r = Math.round(yellowColor.r + (greenColor.r - yellowColor.r) * t)
      g = Math.round(yellowColor.g + (greenColor.g - yellowColor.g) * t)
      b = Math.round(yellowColor.b + (greenColor.b - yellowColor.b) * t)
    }

    return `rgb(${r}, ${g}, ${b})`
  }

  // Calculate coverage percentage and background color for each room
  const roomCoverage = useMemo(() => {
    const coverage = new Map()

    uniqueRooms.forEach((room) => {
      const roomData = chamberRoomsData.get(room.id)
      const rosterSchedule = roomData?.rosterSchedule
      
      if (!rosterSchedule || !rosterSchedule.weeklyHourMap) {
        coverage.set(room.id, { percentage: 0, color: '#ffffff', isOffDay: true })
        return
      }

      const dayHours = rosterSchedule.weeklyHourMap[dayOfWeek] || []
      if (dayHours.length === 0) {
        coverage.set(room.id, { percentage: 0, color: '#ffffff', isOffDay: true })
        return
      }

      // Calculate total scheduled minutes
      let totalMinutes = 0
      dayHours.forEach((range) => {
        const start = dayjs(range.start, 'HH:mm')
        const end = dayjs(range.end, 'HH:mm')
        totalMinutes += end.diff(start, 'minute')
      })

      // Calculate covered minutes from slots
      const slots = slotsByRoom.get(room.id) || []
      let coveredMinutes = 0
      slots.forEach((slot) => {
        const start = dayjs(slot.slotStartTime, 'HH:mm')
        const end = dayjs(slot.slotEndTime, 'HH:mm')
        coveredMinutes += end.diff(start, 'minute')
      })

      const percentage = totalMinutes > 0 ? (coveredMinutes / totalMinutes) * 100 : 0
      const color = getCoverageColor(percentage)

      coverage.set(room.id, { percentage, color, isOffDay: false })
    })

    return coverage
  }, [uniqueRooms, chamberRoomsData, dayOfWeek, slotsByRoom])

  // Calculate available time ranges (roster schedule minus booked slots)
  const getAvailableTimeRanges = (roomId, slots) => {
    const roomData = chamberRoomsData.get(roomId)
    const rosterSchedule = roomData?.rosterSchedule
    
    if (!rosterSchedule || !rosterSchedule.weeklyHourMap) {
      return [] // No schedule = no available time
    }

    const dayHours = rosterSchedule.weeklyHourMap[dayOfWeek] || []
    if (dayHours.length === 0) {
      return [] // Off day = no available time
    }

    // Convert schedule ranges to time objects
    const scheduleRanges = dayHours
      .filter(range => range.start && range.end)
      .map(range => ({
        start: dayjs(range.start, 'HH:mm'),
        end: dayjs(range.end, 'HH:mm'),
      }))
      .sort((a, b) => a.start.isBefore(b.start) ? -1 : 1)

    if (scheduleRanges.length === 0) {
      return []
    }

    // Convert booked slots to time ranges
    const bookedRanges = slots
      .filter(slot => slot.slotStartTime && slot.slotEndTime)
      .map(slot => ({
        start: dayjs(slot.slotStartTime, 'HH:mm'),
        end: dayjs(slot.slotEndTime, 'HH:mm'),
      }))
      .sort((a, b) => a.start.isBefore(b.start) ? -1 : 1)

    // Subtract booked ranges from schedule ranges
    const availableRanges = []
    
    scheduleRanges.forEach((scheduleRange) => {
      let currentStart = scheduleRange.start
      const scheduleEnd = scheduleRange.end

      // Find all booked ranges that overlap with this schedule range
      const overlappingBooked = bookedRanges.filter(booked => 
        booked.start.isBefore(scheduleEnd) && booked.end.isAfter(currentStart)
      )

      if (overlappingBooked.length === 0) {
        // No bookings in this schedule range - entire range is available
        availableRanges.push({
          start: currentStart.format('HH:mm'),
          end: scheduleEnd.format('HH:mm'),
        })
      } else {
        // Process booked ranges in order
        overlappingBooked.forEach((booked) => {
          // If there's a gap before this booking, add it as available
          if (currentStart.isBefore(booked.start)) {
            availableRanges.push({
              start: currentStart.format('HH:mm'),
              end: booked.start.format('HH:mm'),
            })
          }
          // Move current start to after this booking ends
          currentStart = booked.end.isAfter(currentStart) ? booked.end : currentStart
        })

        // If there's remaining time after the last booking, add it
        if (currentStart.isBefore(scheduleEnd)) {
          availableRanges.push({
            start: currentStart.format('HH:mm'),
            end: scheduleEnd.format('HH:mm'),
          })
        }
      }
    })

    return availableRanges.filter(range => {
      // Filter out ranges that are too small (less than 15 minutes)
      const start = dayjs(range.start, 'HH:mm')
      const end = dayjs(range.end, 'HH:mm')
      return end.diff(start, 'minute') >= 15
    })
  }

  const getLegendColor = (mentorId) => {
    if (!legendItems) return null
    const legend = legendItems.find((item) => item.id === mentorId)
    return legend?.bgColor || null
  }

  // Helper function to format event types for display (returns objects with label and id for color lookup)
  const formatEventTypes = (eventTypes) => {
    if (!eventTypes || !Array.isArray(eventTypes) || eventTypes.length === 0) {
      return []
    }
    return eventTypes.map((eventTypeId) => {
      const eventTypeObj = getEventTypeObject(eventTypeId)
      return {
        id: eventTypeId,
        label: eventTypeObj?.title || eventTypeId,
        colors: getEventTypeChipColor(eventTypeId),
      }
    })
  }

  // Helper function to format target specialities for display (returns objects with label and slug for color lookup)
  const formatTargetSpecialities = (targetSpecialitySlugs) => {
    if (!targetSpecialitySlugs || !Array.isArray(targetSpecialitySlugs) || targetSpecialitySlugs.length === 0) {
      return []
    }
    return targetSpecialitySlugs.map((slug) => {
      const speciality = targetSpecialities.find((s) => s.slug === slug)
      return {
        slug,
        label: speciality?.title || slug,
        colors: getTargetSpecialityChipColor(slug),
      }
    })
  }

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
      </Box>
    )
  }

  const handlePrintTable = (specialitySlug) => {
    // Hide all other speciality groups and show only the selected one
    const allGroups = document.querySelectorAll('.roster-speciality-group')
    allGroups.forEach((group) => {
      if (group.getAttribute('data-speciality') !== specialitySlug) {
        group.setAttribute('data-print-hidden', 'true')
        group.style.display = 'none'
      }
    })
    
    // Add style tag for landscape orientation and hiding other tables
    const style = document.createElement('style')
    style.id = 'print-orientation-style'
    style.textContent = `
      @page { size: landscape; margin: 0.3cm; }
      @media print {
        .roster-speciality-group[data-print-hidden="true"] {
          display: none !important;
          visibility: hidden !important;
        }
      }
    `
    document.head.appendChild(style)
    
    // Trigger print
    window.print()
    
    // Restore visibility after printing
    setTimeout(() => {
      allGroups.forEach((group) => {
        group.removeAttribute('data-print-hidden')
        group.style.display = ''
      })
      const printStyle = document.getElementById('print-orientation-style')
      if (printStyle) {
        printStyle.remove()
      }
    }, 1000)
  }

  return (
    <Box sx={{ p: 3, height: '100%', overflow: 'auto' }} className="roster-daily-view">
      {/* Date Navigation */}
      <Box
        className="no-print"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 3,
          p: 2.5,
          backgroundColor: (theme) => theme.palette.mode === 'dark'
            ? 'rgba(30, 30, 30, 0.75)'
            : 'rgba(255, 255, 255, 0.75)',
          backdropFilter: 'blur(10px) saturate(180%)',
          WebkitBackdropFilter: 'blur(10px) saturate(180%)',
          borderRadius: 3,
          boxShadow: 3,
          border: '1px solid',
          borderColor: (theme) => theme.palette.mode === 'dark'
            ? 'rgba(255, 255, 255, 0.1)'
            : 'rgba(0, 0, 0, 0.08)',
        }}
      >
        <IconButton
          onClick={() => onDateChange(selectedDate.subtract(1, 'day'))}
          sx={{ 
            backgroundColor: 'action.hover',
            '&:hover': {
              backgroundColor: 'action.selected',
              transform: 'scale(1.1)',
            },
            transition: 'all 0.2s ease',
          }}
        >
          <ChevronLeftIcon />
        </IconButton>
        <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: '0.5px' }}>
          {selectedDate.format('dddd, MMMM D, YYYY')}
        </Typography>
        <IconButton
          onClick={() => onDateChange(selectedDate.add(1, 'day'))}
          sx={{ 
            backgroundColor: 'action.hover',
            '&:hover': {
              backgroundColor: 'action.selected',
              transform: 'scale(1.1)',
            },
            transition: 'all 0.2s ease',
          }}
        >
          <ChevronRightIcon />
        </IconButton>
      </Box>

      {/* Tables - one per target speciality */}
      {roomsBySpeciality.map((specialityGroup, specialityIndex) => {
        const specialitySlug = specialityGroup.specialitySlug
        const specialityColors = getSpecialityHeaderColor(specialitySlug)
        const specialityTitle = targetSpecialities.find(s => s.slug === specialitySlug)?.title || specialitySlug
        
        return (
          <Box key={specialitySlug} className="roster-speciality-group" data-speciality={specialitySlug} sx={{ mb: specialityIndex < roomsBySpeciality.length - 1 ? 4 : 0 }}>
            {/* Speciality Header */}
            <Box
              className="roster-speciality-header"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                mb: 1.5,
                px: 1,
              }}
            >
              <Chip
                label={specialityTitle}
                sx={{
                  backgroundColor: specialityColors.bg,
                  color: specialityColors.text,
                  border: `2px solid ${specialityColors.border}`,
                  fontWeight: 700,
                  fontSize: '0.9rem',
                  height: '32px',
                  boxShadow: 2,
                }}
              />
              <Typography variant="caption" sx={{ color: 'text.secondary', flex: 1 }}>
                {specialityGroup.rooms.length} room{specialityGroup.rooms.length !== 1 ? 's' : ''}
              </Typography>
              <Tooltip title={`Print ${specialityTitle} table`}>
                <IconButton
                  className="no-print"
                  size="small"
                  onClick={() => handlePrintTable(specialitySlug)}
                  sx={{
                    backgroundColor: specialityColors.bg,
                    color: specialityColors.text,
                    border: `1px solid ${specialityColors.border}`,
                    '&:hover': {
                      backgroundColor: specialityColors.border,
                      color: '#fff',
                    },
                  }}
                >
                  <PrintIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            
            <Paper 
              className="roster-table-container"
              sx={{ 
                overflow: 'auto', 
                boxShadow: 4,
                borderRadius: 3,
                border: '2px solid',
                borderColor: specialityColors.border,
                backgroundColor: (theme) => theme.palette.mode === 'dark'
                  ? 'rgba(30, 30, 30, 0.6)'
                  : 'rgba(255, 255, 255, 0.7)',
                backdropFilter: 'blur(12px) saturate(180%)',
                WebkitBackdropFilter: 'blur(12px) saturate(180%)',
              }}
            >
              <Box sx={{ display: 'table', width: '100%', minWidth: '600px' }}>
                {/* Header */}
                <Box 
                  sx={{ 
                    display: 'table-row', 
                    background: `linear-gradient(135deg, ${specialityColors.border} 0%, ${specialityColors.text} 100%)`,
                    position: 'sticky',
                    top: 0,
                    zIndex: 10,
                  }}
                >
                  <Box
                    sx={{
                      display: 'table-cell',
                      width: '200px',
                      p: 2,
                      fontWeight: 700,
                      color: '#fff',
                      borderRight: '1px solid',
                      borderColor: 'rgba(255, 255, 255, 0.2)',
                      fontSize: '0.875rem',
                      letterSpacing: '0.5px',
                      textTransform: 'uppercase',
                    }}
                  >
                    Room
                  </Box>
                  <Box
                    sx={{
                      display: 'table-cell',
                      p: 2,
                      fontWeight: 700,
                      color: '#fff',
                      fontSize: '0.875rem',
                      letterSpacing: '0.5px',
                      textTransform: 'uppercase',
                    }}
                  >
                    Schedule
                  </Box>
                </Box>

                {/* Rows */}
                {specialityGroup.rooms.map((room) => {
                  const slots = slotsByRoom.get(room.id) || []
                  const coverage = roomCoverage.get(room.id) || { percentage: 0, color: '#ffffff' }
                  const availableRanges = isPastDate ? [] : getAvailableTimeRanges(room.id, slots)
                  const eventTypeBorderColor = getEventTypeBorderColor(room.primaryEventType)
                  
                  // Add thick top border when event type changes
                  const eventTypeBorderStyle = room.isFirstInEventType && room.eventTypeIndex > 0
                    ? `3px solid ${eventTypeBorderColor}`
                    : undefined

            // Combine slots and available ranges, then sort by start time
            const items = []
            
            // Add booked slots
            slots.forEach((slot) => {
              items.push({
                type: 'slot',
                startTime: slot.slotStartTime,
                data: slot,
              })
            })

            // Add available time ranges (only if not past date)
            if (!isPastDate) {
              availableRanges.forEach((range) => {
                items.push({
                  type: 'available',
                  startTime: range.start,
                  data: range,
                })
              })
            }

            // Sort by start time
            items.sort((a, b) => {
              const timeA = dayjs(a.startTime, 'HH:mm')
              const timeB = dayjs(b.startTime, 'HH:mm')
              return timeA.isBefore(timeB) ? -1 : timeA.isAfter(timeB) ? 1 : 0
            })

                  const allItems = items

                  return (
                    <Box key={room.id} sx={{ display: 'table-row' }}>
                      <Box
                        sx={{
                          display: 'table-cell',
                          p: 2,
                          fontWeight: 600,
                          backgroundColor: (theme) => theme.palette.mode === 'dark'
                            ? 'rgba(40, 40, 40, 0.7)'
                            : 'rgba(255, 255, 255, 0.8)',
                          backdropFilter: 'blur(8px)',
                          WebkitBackdropFilter: 'blur(8px)',
                          borderRight: '1px solid',
                          borderBottom: '1px solid',
                          borderTop: eventTypeBorderStyle,
                          borderColor: (theme) => theme.palette.mode === 'dark'
                            ? 'rgba(255, 255, 255, 0.1)'
                            : 'rgba(0, 0, 0, 0.08)',
                          verticalAlign: 'top',
                          position: 'sticky',
                          left: 0,
                          zIndex: 5,
                          boxShadow: '2px 0 8px rgba(0,0,0,0.08)',
                        }}
                      >
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontWeight: 700,
                            fontSize: '0.95rem',
                            color: 'text.primary',
                            mb: 0.5,
                          }}
                        >
                          {room.title || `Room ${room.id}`}
                        </Typography>
                        {room.clinicBrandName && (
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              color: 'text.secondary', 
                              display: 'block', 
                              mt: 0.25,
                              fontSize: '0.75rem',
                              fontWeight: 500,
                            }}
                          >
                            {room.clinicBrandName}
                          </Typography>
                        )}
                        {room.clinicLocationName && (
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              color: 'text.secondary', 
                              display: 'block', 
                              mt: 0.25,
                              fontSize: '0.75rem',
                            }}
                          >
                            {room.clinicLocationName}
                          </Typography>
                        )}
                        {/* Display Event Types only (Target Speciality shown in table header) */}
                        {room.eventTypes && room.eventTypes.length > 0 && (
                          <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {formatEventTypes(room.eventTypes).map((eventType) => (
                              <Chip
                                key={eventType.id}
                                label={eventType.label}
                                size="small"
                                sx={{
                                  height: '20px',
                                  fontSize: '0.65rem',
                                  fontWeight: 600,
                                  backgroundColor: eventType.colors.bg,
                                  color: eventType.colors.text,
                                  border: `1px solid ${eventType.colors.border}`,
                                }}
                              />
                            ))}
                          </Box>
                        )}
                      </Box>
                      <Box
                        sx={{
                          display: 'table-cell',
                          p: 1,
                          minHeight: '100px',
                          borderRight: '1px solid',
                          borderBottom: '1px solid',
                          borderTop: eventTypeBorderStyle,
                          borderColor: 'divider',
                          verticalAlign: 'top',
                          backgroundColor: coverage.color,
                        }}
                      >
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {allItems.length > 0 ? (
                      allItems.map((item, idx) => {
                        if (item.type === 'slot') {
                          // Enhance slot data with mentor profilePicId and color
                          const mentorData = item.data.mentorId ? mentorDataMap.get(item.data.mentorId) : null
                          const mentorColor = getMentorColor(item.data.mentorId)
                          const enhancedSlot = {
                            ...item.data,
                            mentorProfilePicId: item.data.mentorProfilePicId || mentorData?.profilePicId || null,
                            mentorColor, // Pass mentor-specific color
                          }
                          return (
                            <RosterSlotCard
                              key={enhancedSlot.id || `slot-${idx}`}
                              slot={enhancedSlot}
                              color={mentorColor.bg}
                              textColor={mentorColor.text}
                              isUnoccupied={false}
                              onClick={(event) => onSlotClick(enhancedSlot, event)}
                            />
                          )
                        } else {
                          // Available time range
                          const range = item.data
                          return (
                            <Box
                              key={`available-${idx}`}
                              sx={{
                                textAlign: 'center',
                                py: 1,
                                px: 1.5,
                                color: 'primary.main',
                                fontSize: '0.75rem',
                                fontWeight: 500,
                                cursor: 'pointer',
                                border: '2px dashed',
                                borderColor: 'primary.light',
                                borderRadius: 2,
                                backgroundColor: 'rgba(25, 118, 210, 0.05)',
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                '&:hover': {
                                  backgroundColor: 'primary.light',
                                  borderColor: 'primary.main',
                                  color: 'primary.dark',
                                  transform: 'translateY(-2px)',
                                  boxShadow: 2,
                                },
                              }}
                              onClick={(event) => onEmptySlotClick(room.id, dateStr, range.start, event, null)}
                            >
                              <Box sx={{ fontWeight: 600, mb: 0.25 }}>+ Add Slot</Box>
                              <Box sx={{ fontSize: '0.7rem', opacity: 0.9 }}>
                                {range.start} - {range.end}
                              </Box>
                            </Box>
                          )
                        }
                      })
                    ) : (
                      // No items - show appropriate message/icon
                      <Box
                        sx={{
                          textAlign: 'center',
                          py: 1,
                          color: 'text.disabled',
                          fontSize: '0.7rem',
                          fontStyle: 'italic',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {isPastDate && slots.length === 0 ? (
                          <Tooltip title="Past date - cannot be edited">
                            <HistoryIcon sx={{ fontSize: '1.5rem', opacity: 0.5 }} />
                          </Tooltip>
                        ) : slots.length === 0 ? (
                          'Clinic Closed'
                        ) : (
                          'No available time'
                        )}
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Box>
                )
              })}
              </Box>
            </Paper>
          </Box>
        )
      })}
    </Box>
  )
}
